import {
  type ConversationGroupMessageType,
  ConversationMessageContentType,
  type ConversationMessageType,
  type ConversationMessageTypeUnion,
} from '@/models/conversations/conversation';
import { type PeriodicMessageStoreState, usePeriodicMessageStore } from '@/stores/periodic-message';
import { generateSessionKeyFromMessage } from '@/utils/session';

export const isDeleted = (
  message: Pick<
    ConversationMessageTypeUnion,
    'ID' | 'Status' | 'ReadDelete' | 'AutoDeleteTime' | 'ReadStamp' | 'MsgType'
  > &
    (Pick<ConversationMessageType, 'ChatSessionID'> | Pick<ConversationGroupMessageType, 'GroupID'>),
) => {
  if (message.ReadDelete) return isBurned(message);
  return isDeletedById(generateSessionKeyFromMessage(message), message.ID) || message.Status === false;
};

export const isDeletedById = (
  key: string,
  messageId: number,
  store?: Pick<PeriodicMessageStoreState, 'deletedMessageIds' | 'purgedMessageIds'>,
) => {
  const { deletedMessageIds, purgedMessageIds } = store ?? usePeriodicMessageStore.getState();

  if (deletedMessageIds[key]?.includes(messageId)) return true;
  if (purgedMessageIds[key]) return purgedMessageIds[key] >= messageId;
  return false;
};

type BurnableMessage = Pick<
  ConversationMessageTypeUnion,
  'MsgType' | 'IsRead' | 'ReadDelete' | 'AutoDeleteTime' | 'ReadStamp'
>;
export const isBurnable = (message: BurnableMessage): message is Required<BurnableMessage> =>
  Boolean(
    message.ReadDelete &&
      message.MsgType !== ConversationMessageContentType.Notice &&
      message.IsRead &&
      message.ReadStamp &&
      message.AutoDeleteTime,
  );

export const isBurned = (message: BurnableMessage, time = Date.now()) =>
  isBurnable(message) && message.ReadStamp + message.AutoDeleteTime * 1000 <= time;

export const isBurning = (message: BurnableMessage, time = Date.now()) =>
  isBurnable(message) && message.ReadStamp + message.AutoDeleteTime * 1000 > time;

export const isMedia = (message: Pick<ConversationMessageTypeUnion, 'MsgType'>) =>
  [
    ConversationMessageContentType.Image,
    ConversationMessageContentType.Video,
    ConversationMessageContentType.File,
  ].includes(message.MsgType);
